import React, { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../../utils/apiClient';

/**
 * SharePointBrowser - lightweight folder browser over the project's SharePoint
 * site (via backend Graph proxy) to pick a GA drawing without downloading and
 * re-uploading it manually.
 */
const SharePointBrowser = ({
  jobNumber,
  onPick,
  onClose,
  accept = /\.(pdf|png|jpe?g|webp)$/i,
  browseUrl,
  title,
}) => {
  const { instance, accounts } = useMsal();
  const [path, setPath] = useState('');
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ sitePath: `/sites/${jobNumber}`, driveName: 'Team' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('');
  const apiBase = browseUrl || `/ga-dashboards/${jobNumber}/sharepoint/browse`;

  const load = useCallback(async (folderPath) => {
    try {
      setLoading(true);
      setError(null);
      const res = await authenticatedApiCall(
        instance, accounts, 'GET',
        `${apiBase}?path=${encodeURIComponent(folderPath)}`
      );
      setItems(res.data?.data?.items || []);
      setMeta({ sitePath: res.data?.data?.sitePath, driveName: res.data?.data?.driveName });
      setPath(folderPath);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to browse SharePoint');
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, apiBase]);

  useEffect(() => { load(''); }, [load]);

  const crumbs = path ? path.split('/') : [];

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 12, width: 640, maxWidth: '94vw', maxHeight: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '14px 18px', background: '#0f766e', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <strong>{title || `Pick drawing from SharePoint (${jobNumber})`}</strong>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer' }}>×</button>
        </div>

        <div style={{ padding: '8px 18px', fontSize: 12, color: '#475569', borderBottom: '1px solid #e2e8f0', display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          <span onClick={() => load('')} style={{ cursor: 'pointer', color: '#0f766e', fontWeight: 700 }}>root</span>
          {crumbs.map((c, i) => (
            <span key={i}>
              {' / '}
              <span onClick={() => load(crumbs.slice(0, i + 1).join('/'))} style={{ cursor: 'pointer', color: '#0f766e' }}>{c}</span>
            </span>
          ))}
        </div>

        <div style={{ padding: '8px 18px', borderBottom: '1px solid #e2e8f0' }}>
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter this folder…"
            style={{ width: '100%', border: '1px solid #cbd5e1', borderRadius: 6, padding: '6px 8px', fontSize: 13 }}
          />
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: '8px 12px' }}>
          {loading && <div style={{ padding: 24, textAlign: 'center', color: '#64748b' }}>Loading…</div>}
          {error && <div style={{ padding: 16, color: '#dc2626' }}>{error}</div>}
          {!loading && items.filter((it) => {
            const q = filter.trim().toLowerCase();
            if (!q) return true;
            return String(it.name || '').toLowerCase().includes(q);
          }).map((it) => {
            const pickable = !it.isFolder && accept.test(it.name);
            return (
              <div
                key={it.id}
                onClick={() => {
                  if (it.isFolder) load(path ? `${path}/${it.name}` : it.name);
                  else if (pickable) onPick({ ...meta, itemId: it.id, name: it.name });
                }}
                style={{
                  padding: '7px 10px', borderRadius: 6, fontSize: 13, display: 'flex', justifyContent: 'space-between',
                  cursor: it.isFolder || pickable ? 'pointer' : 'default',
                  opacity: it.isFolder || pickable ? 1 : 0.45,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#f1f5f9'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <span>{it.isFolder ? '📁' : '📄'} {it.name}</span>
                <span style={{ color: '#94a3b8', fontSize: 11 }}>
                  {it.isFolder ? '' : `${Math.round((it.size || 0) / 1024).toLocaleString()} KB`}
                </span>
              </div>
            );
          })}
          {!loading && !items.length && !error && (
            <div style={{ padding: 24, textAlign: 'center', color: '#64748b' }}>Empty folder</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SharePointBrowser;
